/**
 * Utilitários para manipulação de contatos WhatsApp
 */

import i18n from '@/i18n'
import { formatDateInAccountTz, startOfAccountDay } from '@/lib/accountTimezone'

/**
 * Formata o JID do WhatsApp para exibição
 */
export const formatWhatsAppJid = (jid: string): string => {
  if (!jid) return 'N/A'

  // Remove sufixos do WhatsApp
  const cleanJid = jid.replace('@s.whatsapp.net', '').replace('@c.us', '').replace('@g.us', '')

  if (!/^\d+$/.test(cleanJid)) {
    return cleanJid
  }

  // BR mask only when the number clearly looks Brazilian (+55 + DDD + 8/9 digits).
  // Other DDIs must not assume a 2-digit country code (breaks +1, +351, +591, etc.).
  if (cleanJid.startsWith('55') && (cleanJid.length === 12 || cleanJid.length === 13)) {
    const area = cleanJid.slice(2, 4)
    const number = cleanJid.slice(4)

    if (number.length === 9) {
      return `+55 (${area}) ${number.slice(0, 5)}-${number.slice(5)}`
    }
    if (number.length === 8) {
      return `+55 (${area}) ${number.slice(0, 4)}-${number.slice(4)}`
    }
  }

  return `+${cleanJid}`
}

/**
 * Determina se o contato é um grupo
 */
export const isGroupContact = (jid: string): boolean => {
  return jid.endsWith('@g.us')
}

/**
 * Extrai o nome de exibição do contato
 */
export const getDisplayName = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  jid: string
}): string => {
  if (contact.name) {
    return contact.name
  }

  if (contact.display_name) {
    return contact.display_name
  }

  if (contact.push_name_last) {
    return contact.push_name_last
  }

  // Fallback para o JID formatado
  return formatWhatsAppJid(contact.jid)
}

/**
 * Formata a data da última mensagem
 */
export const formatLastMessageDate = (lastMessageAt: string | null): string => {
  const t = i18n.getFixedT(null, 'common')

  if (!lastMessageAt) {
    return t('time.never')
  }

  const date = new Date(lastMessageAt)
  const now = new Date()

  // Compara dias do calendário no fuso da conta (não diferença em horas) para evitar que
  // mensagens enviadas ontem apareçam como "Hoje" quando faz menos de 24h.
  const msPerDay = 1000 * 60 * 60 * 24
  const diffInDays = Math.round(
    (startOfAccountDay(now).getTime() - startOfAccountDay(date).getTime()) / msPerDay
  )

  if (diffInDays <= 0) {
    return t('time.today')
  } else if (diffInDays === 1) {
    return t('time.yesterday')
  } else if (diffInDays < 7) {
    return t('time.daysAgoShort', { count: diffInDays })
  } else {
    return formatDateInAccountTz(date)
  }
}

/**
 * Obtém o telefone formatado do contato
 * Para exportação: retorna "none" se não houver nome
 */
export const getContactPhone = (contact: {
  phone?: string | null
  jid: string
}): string => {
  if (contact.phone) {
    return contact.phone
  }

  return formatWhatsAppJid(contact.jid)
}

/**
 * Obtém o telefone formatado para exportação
 * Retorna "none" se o contato não tiver nome definido
 */
export const getContactPhoneForExport = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  phone?: string | null
  jid: string
}): string => {
  // Verifica se o contato tem algum nome definido
  const hasName = contact.name || contact.display_name || contact.push_name_last

  // Se não tiver nome, retorna "none"
  if (!hasName) {
    return 'none'
  }

  // Se tiver nome, retorna o telefone normalmente
  if (contact.phone) {
    return contact.phone
  }

  return formatWhatsAppJid(contact.jid)
}

/**
 * Gera URL de avatar baseado em profile picture ou iniciais
 * Tenta usar a foto real se disponível, senão retorna SVG com iniciais
 */
export const getContactAvatarUrl = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  jid: string
  profile_picture?: string | null
}): string => {
  // Se tiver profile_picture, usar ela
  if (contact.profile_picture) {
    return contact.profile_picture
  }

  // Senão, gerar SVG com iniciais
  return generateContactAvatar(contact)
}

/**
 * Gera avatar baseado no nome ou JID
 */
export const generateContactAvatar = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  jid: string
}): string => {
  const name = getDisplayName(contact)
  const asciiLetterRegex = /[A-Za-z0-9\u00C0-\u024F]/
  const rawInitials = name
    .split(' ')
    .map(word => {
      for (const char of word) {
        if (asciiLetterRegex.test(char)) return char
      }
      return ''
    })
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const initials = rawInitials || (contact.jid ? contact.jid.replace(/\D/g, '').slice(-2) || '?' : '?')

  const backgroundColor = '#a1a1aa'
  const textColor = '#000000'

  const svg = `<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="20" fill="${backgroundColor}"/><text x="20" y="26" text-anchor="middle" fill="${textColor}" font-family="Arial" font-size="14" font-weight="bold">${initials}</text></svg>`

  try {
    return `data:image/svg+xml,${encodeURIComponent(svg)}`
  } catch {
    const fallbackSvg = `<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="20" fill="${backgroundColor}"/><text x="20" y="26" text-anchor="middle" fill="${textColor}" font-family="Arial" font-size="14" font-weight="bold">?</text></svg>`
    return `data:image/svg+xml,${encodeURIComponent(fallbackSvg)}`
  }
}

/**
 * Determina se o contato tem mensagens não lidas, comparando
 * `last_message_at` com `last_read_at`. A regra é: existe mensagem
 * (last_message_at definido) e o último "read" é nulo OU é anterior.
 */
export const isContactUnread = (contact: {
  last_message_at?: string | null
  last_read_at?: string | null
}): boolean => {
  if (!contact.last_message_at) return false
  if (!contact.last_read_at) return true
  return new Date(contact.last_read_at).getTime() < new Date(contact.last_message_at).getTime()
}

/**
 * Formata a data de cadastro do contato
 */
export const formatCreatedDate = (createdAt: string | null): string => {
  if (!createdAt) {
    return i18n.getFixedT(null, 'common')('time.unknownDate')
  }

  return formatDateInAccountTz(createdAt, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

/**
 * Valida se uma string é um UUID válido
 */
export const isValidUUID = (uuid: string): boolean => {
  if (!uuid || typeof uuid !== 'string') {
    return false
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(uuid)
}

/**
 * Formata telefone internacional com código do país
 */
export const formatInternationalPhone = (phone: string, countryCode: string = '55'): string => {
  // Remove caracteres não numéricos
  const cleanPhone = phone.replace(/\D/g, '')

  // Se já começa com o código do país, retorna como está
  if (cleanPhone.startsWith(countryCode)) {
    return cleanPhone
  }

  // Adiciona o código do país
  return `${countryCode}${cleanPhone}`
}

/** Contato individual com mesmo número pode vir em mais de uma linha (JID @c.us vs @s.whatsapp.net, etc.). */
export type ContactDedupeFields = {
  id: string
  jid: string
  phone?: string | null
  is_group?: boolean | null
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  last_message_at?: string | null
}

function digitsForCanonicalMatch(contact: Pick<ContactDedupeFields, 'jid' | 'phone'>): string | null {
  const jid = contact.jid || ''
  if (jid.endsWith('@g.us')) return null
  if (jid.includes('@lid')) return null

  const fromJid = jid.replace(/\D/g, '')
  if (fromJid.length >= 8) return fromJid

  const fromPhone = (contact.phone || '').replace(/\D/g, '')
  if (fromPhone.length >= 8) return fromPhone

  return null
}

/**
 * Chave estável para agrupar o mesmo número WhatsApp entre formatos de JID diferentes.
 */
export function getContactCanonicalKey(contact: Pick<ContactDedupeFields, 'jid' | 'phone' | 'is_group'>): string {
  const jid = contact.jid || ''
  if (contact.is_group || jid.endsWith('@g.us')) {
    return `group:${jid}`
  }
  if (jid.includes('@lid')) {
    return `lid:${jid}`
  }
  const digits = digitsForCanonicalMatch(contact)
  if (digits) return `pn:${digits}`
  return `jid:${jid}`
}

export type ContactDuplicateLookupProbe = Pick<
  ContactDedupeFields,
  'jid' | 'phone' | 'is_group'
> & {
  /** DDI digitado na UI — só no lookup; nunca inferir país só pelos dígitos. */
  countryCallingCode?: string | null
}

/**
 * Variações de `phone` e `jid` possivelmente gravadas no banco para o mesmo número (BR: com/sem 55, 9 da operadora, @s.whatsapp.net vs @c.us).
 * Usado antes de inserir contato para evitar duplicata.
 * Variantes BR só quando countryCallingCode === "55" ou o número já é E.164 BR inequívoco (55… length ≥ 12).
 */
export function buildContactDuplicateLookupVariants(
  contact: ContactDuplicateLookupProbe
): { phones: string[]; jids: string[] } {
  const jid = (contact.jid || '').trim()
  const key = getContactCanonicalKey(contact)

  if (contact.is_group || jid.endsWith('@g.us')) {
    return jid ? { phones: [], jids: [jid] } : { phones: [], jids: [] }
  }
  if (jid.includes('@lid')) {
    return jid ? { phones: [], jids: [jid] } : { phones: [], jids: [] }
  }
  if (key.startsWith('jid:')) {
    const j = key.slice(4)
    return j ? { phones: [], jids: [j] } : { phones: [], jids: [] }
  }
  if (!key.startsWith('pn:')) {
    return jid ? { phones: [], jids: [jid] } : { phones: [], jids: [] }
  }

  const D = key.slice(3)
  const countryCallingCode = (contact.countryCallingCode || '').replace(/\D/g, '') || null
  const phones = expandPhoneDigitVariantsForLookup(D, countryCallingCode)
  const jids = new Set<string>()
  for (const p of phones) {
    if (p.length >= 8) {
      jids.add(`${p}@s.whatsapp.net`)
      jids.add(`${p}@c.us`)
    }
  }
  if (jid) jids.add(jid)

  return {
    phones: [...phones].filter((p) => p.length >= 8),
    jids: [...jids],
  }
}

function addBrazilianDigitVariants(out: Set<string>, D: string): void {
  out.add(D)

  if (D.startsWith('55') && D.length >= 12) {
    const rest = D.slice(2)
    out.add(rest)
    if (rest.length === 11 && rest[2] === '9') {
      out.add(rest.slice(0, 2) + rest.slice(3))
    }
    if (rest.length === 10) {
      out.add(rest.slice(0, 2) + '9' + rest.slice(2))
    }
    return
  }

  // National BR (10/11) when DDI is known to be 55
  if ((D.length === 10 || D.length === 11) && !D.startsWith('55')) {
    out.add(`55${D}`)
    if (D.length === 11 && D[2] === '9') {
      out.add(`55${D.slice(0, 2)}${D.slice(3)}`)
    }
    if (D.length === 10) {
      out.add(`55${D.slice(0, 2)}9${D.slice(2)}`)
    }
  }
}

/**
 * Expande dígitos para lookup de duplicata.
 * - DDI 55 → variantes BR
 * - outro DDI → só o número exato
 * - sem DDI, mas 55… com length ≥ 12 → variantes BR (registro inequívoco)
 * - sem contexto → só D (não prefixar 55 por length)
 */
function expandPhoneDigitVariantsForLookup(
  D: string,
  countryCallingCode: string | null
): Set<string> {
  const out = new Set<string>()
  if (!D || !/^\d{8,}$/.test(D)) {
    if (D) out.add(D)
    return out
  }

  const isExplicitBr = countryCallingCode === '55'
  const isUnequivocalBrDigits = !countryCallingCode && D.startsWith('55') && D.length >= 12
  const isExplicitInternational =
    Boolean(countryCallingCode) && countryCallingCode !== '55'

  if (isExplicitInternational) {
    out.add(D)
    return out
  }

  if (isExplicitBr || isUnequivocalBrDigits) {
    addBrazilianDigitVariants(out, D)
    return out
  }

  // Sem contexto confiável de país: não inventar variantes BR
  out.add(D)
  return out
}

function contactQualityScore(contact: ContactDedupeFields): number {
  let s = 0
  const t = contact.last_message_at ? new Date(contact.last_message_at).getTime() : 0
  s += t / 1e15
  if (contact.name || contact.display_name || contact.push_name_last) s += 10
  if (contact.jid.endsWith('@s.whatsapp.net')) s += 0.5
  return s
}

function pickBetterDuplicate<T extends ContactDedupeFields>(a: T, b: T): T {
  const da = contactQualityScore(a)
  const db = contactQualityScore(b)
  if (Math.abs(db - da) > 1e-9) return db >= da ? b : a
  return a.id.localeCompare(b.id) <= 0 ? a : b
}

/**
 * Remove duplicatas visuais (mesmo número / mesmo grupo), mantendo o registro “melhor”
 * (última mensagem mais recente, nome disponível, JID @s.whatsapp.net).
 */
export function dedupeAccountContacts<T extends ContactDedupeFields>(contacts: T[]): T[] {
  const byKey = new Map<string, T>()
  for (const c of contacts) {
    const key = getContactCanonicalKey(c)
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, c)
      continue
    }
    byKey.set(key, pickBetterDuplicate(existing, c))
  }
  return Array.from(byKey.values()).sort((a, b) => {
    const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0
    const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0
    return bTime - aTime
  })
}
/**
 * Aplica na lista o efeito que o trigger do banco aplica na linha: a chegada de uma
 * mensagem sobe o `last_message_at` da conversa.
 *
 * Existe porque o evento de UPDATE de `account_contacts` pode se perder (reconexão, aba
 * suspensa, RLS derrubando entrega sob carga) enquanto o INSERT da mensagem chega. Sem
 * isso, `last_message_at` fica igual ao `last_read_at` e a conversa parece lida mesmo
 * tendo mensagem nova — a bolinha azul não aparece.
 *
 * Devolve o MESMO array quando não há nada a fazer, para o React não re-renderizar.
 */
export function applyMessageActivity<T extends ContactDedupeFields>(
  contacts: T[],
  contactId: string,
  sentAt: string
): T[] {
  const current = contacts.find((c) => c.id === contactId)
  // Fora da página carregada: quem cuida disso é o fetch, não este atalho.
  if (!current) return contacts

  // Só avança. Um evento fora de ordem não pode puxar a conversa para trás — nem
  // reabrir uma bolinha que o usuário já leu.
  if (current.last_message_at && current.last_message_at >= sentAt) return contacts

  const updated = { ...current, last_message_at: sentAt }
  const others = contacts.filter((c) => c.id !== contactId)
  return dedupeAccountContacts([updated, ...others])
}
